"use client";

import RouterLink from "@/components/ui/router-link";

import { useScopedI18n } from "@/messages/client";
import { X } from "lucide-react";
import SearchTabs from "./search-tabs";
import { Filter, SearchData } from "./search-content";
import { match } from "ts-pattern";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { SearchResponse } from "meilisearch";
import { useCurrentLocale } from "@/hooks/useCurrentLocale";
import { Button } from "@/components/ui/button";
import useSWRInfinite from "swr/infinite";
import { getIndex, SearchConfig } from "@/server/meilisearch";
import Pagination from "./search-pagination";

const host = process.env.NEXT_PUBLIC_URL ?? "";

function generateFilter(params: {
  category: "all" | "product" | "application" | "news";
  filter?: Filter[];
}) {
  const filterArray = [];

  if (params.category !== "all") {
    filterArray.push(`type = '${params.category}'`);
  }

  if (params.filter?.length) {
    const product = params.filter?.find((i) => i.key === "product");

    const application = params.filter.find((i) => i.key === "application");

    const news = params.filter.find((i) => i.key === "news");

    const techDoc = params.filter.find((i) => i.key === "techDoc");

    const appNote = params.filter.find((i) => i.key === "appNote");

    const evmGuide = params.filter.find((i) => i.key === "evmGuide");
    if (news) {
      filterArray.push(`newsCategory = '${news.value}'`);
    }

    if (product?.level === 1 || application?.level === 1) {
      filterArray.push(["type = 'application'", "type = 'product'"]);

      if (product?.level === 1) {
        filterArray.push(`productCategory1 = '${product.value}'`);
      }

      if (application?.level === 1) {
        filterArray.push(`applicationCategory1 = '${application.value}'`);
      }
    }

    if (product?.level && product.level > 1) {
      filterArray.push(`productCategory${product.level} = '${product.value}'`);
    }

    const techDocArray = [];

    if (appNote) {
      techDocArray.push("appNote EXISTS");
    }

    if (evmGuide) {
      techDocArray.push("evmGuide EXISTS");
    }

    if (techDoc) {
      techDocArray.push(`techDoc${techDoc.level} EXISTS`);
    }

    if (techDocArray.length > 0) {
      filterArray.push(techDocArray);
    }

    if (application?.level === 2) {
      filterArray.push(`applicationCategory2 = '${application.value}'`);
    }
  }

  return filterArray;
}

export default function SearchList(props: {
  filter: Filter[];
  setFilter: Dispatch<SetStateAction<Filter[]>>;
  data?: SearchResponse<SearchData>;
  site: number;
  lang: number;
  keyword: string;
  sort: string;
  isMobile?: boolean;
  category: "all" | "product" | "application" | "news";
  setCategory: Dispatch<
    SetStateAction<"all" | "product" | "application" | "news">
  >;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  isLoading: boolean;
  searchConfig: SearchConfig
}) {
  const { setPage } = props;

  const t = useScopedI18n("全局搜索");

  const locale = useCurrentLocale();

  const {
    data: mobileData,
    setSize,
    size,
    isValidating,
    isLoading,
  } = useSWRInfinite(
    (page) =>
      props.isMobile
        ? [
            `/api/mobile/search`,
            page,
            props.site,
            props.lang,
            props.keyword,
            JSON.stringify(props.filter),
            props.sort,
            props.category,
          ]
        : null,
    async (key) => {
      const page = key?.[1] + 1;

      const client = getIndex(props.searchConfig, props.site);

      const filterArray = generateFilter({
        category: props.category,
        filter: props.filter,
      });

      const searchData = (await client.search<SearchData>(props.keyword || "", {
        page: Number(page),
        hitsPerPage: 10,
        filter: filterArray,
        sort: props.sort === "date" ? ["publishDate:desc"] : ["type:desc"],
      })) as SearchResponse<SearchData>;

      return searchData;
    },
    {
      initialSize: 1,
    }
  );

  const data = useMemo(() => {
    if (!props.isMobile) return props.data?.hits ?? [];

    return mobileData ? mobileData.flatMap((i) => i.hits) : [];
  }, [props.isMobile, props.data, mobileData]);

  const getUrl = useCallback(
    (type: string, url: string) => {
      const localeHost = `${host}${locale === "en" ? "" : "/cn"}`;

      return match(type)
        .with("product", () => `${localeHost}/product/${url}`)
        .with("news", () => `${localeHost}/news/${url}`)
        .with("application", () => `${localeHost}/application/${url}`)
        .otherwise(() => "");
    },
    [locale]
  );

  const isProduct = useMemo(() => {
    const application = props.filter?.find((i) => i.key === "application");
    return (
      !props.filter?.find((i) => i.key === "news") &&
      (application?.level ?? 1) === 1
    );
  }, [props.filter]);

  const isApplication = useMemo(() => {
    const product = props.filter?.find((i) => i.key === "product");
    return (
      !props.filter?.find((i) => i.key === "news") &&
      (product?.level ?? 1) === 1
    );
  }, [props.filter]);

  const isNews = useMemo(() => {
    return (
      !props.filter?.length || !props.filter?.findIndex((i) => i.key === "news")
    );
  }, [props.filter]);

  const onPageChange = useCallback(
    (page: number) => {
      setPage(page);

      // 滚动到顶部
      window.scrollTo(0, 0);
    },
    [setPage]
  );

  const totalRef = useRef<number>(props.data?.totalHits ?? 0);

  useEffect(() => {
    if (props.data?.totalHits !== undefined) {
      totalRef.current = props.data.totalHits;
    }
  }, [props.data?.totalHits]);

  const isLoadingMore = isLoading || isValidating;
  const isEmpty = mobileData?.[0]?.hits?.length === 0;
  const isReachingEnd =
    isEmpty || data.length >= (mobileData?.[0]?.totalHits ?? 0);

  return (
    <div className="relative flex-1">
      <div className="hidden items-center border-b border-[#A6A6A6] pb-1 lg:flex">
        <div className="text-xl font-medium text-[#A6A6A6]">
          {t<any, any>("搜索结果数量展示", {
            count: props.data?.totalHits ?? 0,
          })}
        </div>
      </div>

      {props.filter?.length > 0 && (
        <div className="my-4 hidden lg:block">
          <div className="w-full space-y-3 rounded-md bg-[#F4F4F4] px-4 py-3">
            {props.filter?.map((item) => (
              <div key={item.value} className="flex items-center space-x-6">
                <div className="text-lg font-medium text-[#383838]">
                  {match(item.type)
                    .with("product", () => t("产品"))
                    .with("news", () => t("新闻"))
                    .with("application", () => t("应用"))
                    .with("techDoc", () => t("技术文档"))
                    .otherwise(() => t("全部"))}
                </div>

                <div className="flex items-center space-x-3 rounded-3xl border bg-white px-3 py-0.5 text-sm">
                  <div>{item.label?.join(" / ")}</div>

                  <div
                    className="cursor-pointer transition-colors duration-300 hover:text-primary"
                    onClick={() => {
                      props.setFilter((prev) =>
                        prev.filter((i) => i.key !== item.key)
                      );
                    }}>
                    <X size={16} />
                  </div>
                </div>
              </div>
            ))}

            <div className="flex justify-end">
              <button
                className="text-sm text-primary transition-colors duration-300 hover:text-primary/80"
                onClick={() => {
                  props.setFilter([]);
                }}>
                {t("清除所有")}
              </button>
            </div>
          </div>
        </div>
      )}

      <SearchTabs
        data={[
          {
            tabName: t("全部"),
            value: "all",
          },
          {
            tabName: t("产品"),
            value: "product",
            hide: !isProduct,
          },
          {
            tabName: t("应用"),
            value: "application",
            hide: !isApplication,
          },
          {
            tabName: t("新闻"),
            value: "news",
            hide: !isNews,
          },
        ]}
        current={props.category}
        setCurrent={(key) => {
          props.setCategory(key as "all" | "product" | "application" | "news");
          setSize(1);
        }}
      />

      <div className="space-y-3 bg-[#F7F7F7] px-3 py-4 md:space-y-0 lg:mt-2 lg:bg-white lg:px-0 lg:py-0">
        {data?.map((item) => (
          <div
            key={item.id}
            className="flex h-[110px] flex-col justify-between rounded-xl bg-white p-3 shadow-[0px_2px_8px_rgba(0,0,0,0.1)] lg:justify-center lg:rounded-none lg:border-b lg:border-[#A6A6A6] lg:p-0 lg:shadow-none">
            <div className="text-xs font-medium text-[#2C2C2C]">
              {match(item.type)
                .with("product", () => (
                  <span>
                    {t("产品类别")}{" "}
                    {item?.productCategory3
                      ? `(${item?.productCategory3})`
                      : ""}
                  </span>
                ))
                .with("news", () => (
                  <span>
                    {t("新闻类别")}{" "}
                    {item?.newsCategory ? `(${item?.newsCategory})` : ""}
                  </span>
                ))
                .with("application", () => (
                  <span>
                    {t("应用类别")}{" "}
                    {item?.applicationCategory2
                      ? `(${item?.applicationCategory2})`
                      : ""}
                  </span>
                ))

                .otherwise(() => null)}
            </div>
            <RouterLink
              target="_blank"
              href={getUrl(item.type, item.url ?? "")}
              className="line-clamp-2 cursor-pointer py-0.5 text-base text-[#2967E3] underline-offset-4 transition-all duration-300 hover:underline">
              {item.title}
            </RouterLink>
            <RouterLink
              href={getUrl(item.type, item.url ?? "")}
              className="truncate text-xs text-[#A6A6A6] lg:text-[#2C2C2C]">
              {getUrl(item.type, item.url ?? "")}
            </RouterLink>
          </div>
        ))}

        <div className="flex justify-center pb-8 pt-6 md:hidden">
          <Button
            variant="outline"
            className="h-8 rounded-3xl px-5 text-sm"
            loading={isValidating}
            disabled={isLoadingMore || isReachingEnd}
            onClick={() => setSize(size + 1)}>
            {isLoadingMore
              ? t("加载中")
              : isReachingEnd
                ? t("无更多数据")
                : t("点击加载更多")}
          </Button>
        </div>
      </div>

      <div className="hidden py-14 md:block">
        <Pagination
          total={props.data?.totalHits ?? totalRef.current ?? 0}
          pageSize={10}
          current={props.page}
          onChange={onPageChange}
        />
      </div>
    </div>
  );
}
